import { useEffect, useState } from "react";
import { Loader2, Sparkles, Wrench, Search, PhoneCall, User, FileDown, Mail } from "lucide-react";
import { toast } from "sonner";
import { MailComposer } from "@/pages/vesta/VestaMail";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dtt } from "./shared";
import { AiError } from "./AiJob";

const GRAV = { bassa: "#22c55e", media: "#eab308", alta: "#ef4444" };

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400"><Icon className="h-3.5 w-3.5" />{title}</div>
    {children}
  </div>
);

const btn = "inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50";

const draftOf = (d, plantName) => ({
  subject: `Relazione tecnica · ${d.titolo || "allarme"}${plantName ? ` · ${plantName}` : ""}`,
  body: [`Buongiorno,`, `in allegato la relazione tecnica dell'allarme ${d.titolo || ""}${plantName ? ` sull'impianto ${plantName}` : ""}.`,
    d.diagnosi && `Diagnosi: ${d.diagnosi}`, d.intervento?.length && `Intervento suggerito:\n${d.intervento.map((x, i) => `${i + 1}. ${x}`).join("\n")}`,
    `Cordiali saluti`].filter(Boolean).join("\n\n"),
});

function Actions({ url, params, d, plantName, accent }) {
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState(null);
  const pdf = async () => {
    setBusy(true);
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`${url}/relazione.pdf`, { params, responseType: "blob", timeout: 120000 });
      const u = URL.createObjectURL(r.data);
      if (w) w.location.href = u; else window.location.href = u;
    } catch { w?.close(); toast.error("PDF non disponibile"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="alarm-analysis-actions">
      <button type="button" onClick={pdf} disabled={busy} className={btn} data-testid="alarm-analysis-pdf">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: accent }} /> : <FileDown className="h-3.5 w-3.5" />}PDF</button>
      <button type="button" onClick={() => setMail(draftOf(d, plantName))} className={btn} data-testid="alarm-analysis-email"><Mail className="h-3.5 w-3.5" />Email</button>
      {mail && <MailComposer draft={mail} onClose={() => setMail(null)} sendUrl={`${url}/relazione/email`} sendParams={params} attachment="Relazione tecnica (PDF)" />}
    </div>
  );
}

export function AlarmAnalysis({ base, params, plantId, alarm, onClose, accent, plantName }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [run, setRun] = useState(0);
  useEffect(() => {
    if (!alarm) return;
    setD(null); setErr("");
    api.post(`${base}/plants/${encodeURIComponent(plantId)}/alarms/${encodeURIComponent(alarm.code)}/analisi`, null, { params, timeout: 80000 })
      .then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Analisi non disponibile, riprova"));
  }, [alarm, run]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!alarm} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="alarm-analysis-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg" style={{ color: accent }}>
            <Sparkles className="h-5 w-5" /><span data-testid="alarm-analysis-title">{d?.titolo || `${alarm?.code} · ${alarm?.sigla || ""} ${alarm?.desc || ""}`}</span>
          </DialogTitle>
          {d && <Actions url={`${base}/plants/${encodeURIComponent(plantId)}/alarms/${encodeURIComponent(alarm.code)}`} params={params} d={d} plantName={plantName} accent={accent} />}
          {alarm && <p className="text-xs text-neutral-400">{[alarm.model, alarm.sn && `SN ${alarm.sn}`, alarm.occorrenze && `${alarm.occorrenze} volte`, `ultimo ${dtt(alarm.at)}`].filter(Boolean).join(" · ")}</p>}
        </DialogHeader>
        {err ? <AiError error={err} onRetry={() => setRun((x) => x + 1)} accent={accent} testid="alarm-analysis-error" /> : !d ? (
          <div className="flex items-center gap-2 py-6 text-sm text-neutral-400" data-testid="alarm-analysis-loading"><Loader2 className="h-5 w-5 animate-spin" style={{ color: accent }} />Analisi AI in corso…</div>
        ) : (
          <div className="space-y-5" data-testid="alarm-analysis-body">
            {d.manuali_avviso && <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="alarm-analysis-docs-warning">{d.manuali_avviso}: l'analisi usa l'ultima versione dei manuali disponibile.</p>}
            <span className="inline-block rounded-full border px-3 py-0.5 text-[10px] font-bold uppercase" style={{ borderColor: GRAV[d.gravita], color: GRAV[d.gravita] }} data-testid="alarm-analysis-gravita">Gravità {d.gravita}</span>
            <Section icon={Search} title="Diagnosi" testid="alarm-analysis-diagnosi"><p className="text-sm leading-relaxed text-neutral-200">{d.diagnosi}</p></Section>
            {!!d.cause_probabili?.length && <Section icon={Search} title="Cause probabili" testid="alarm-analysis-cause"><ul className="list-disc space-y-1 pl-5 text-sm text-neutral-200">{d.cause_probabili.map((c, i) => <li key={i}>{c}</li>)}</ul></Section>}
            <Section icon={Wrench} title="Intervento suggerito" testid="alarm-analysis-intervento"><ol className="list-decimal space-y-1 pl-5 text-sm text-neutral-200">{(d.intervento || []).map((c, i) => <li key={i}>{c}</li>)}</ol></Section>
            {d.quando_assistenza && <Section icon={PhoneCall} title="Quando contattare l'assistenza" testid="alarm-analysis-assistenza"><p className="text-sm text-neutral-200">{d.quando_assistenza}</p></Section>}
            {d.per_il_cliente && <Section icon={User} title="Da dire al cliente" testid="alarm-analysis-cliente"><p className="rounded-lg border border-neutral-800 bg-black/40 p-3 text-sm italic text-neutral-300">{d.per_il_cliente}</p></Section>}
            <p className="text-[11px] text-neutral-600">Analisi generata con AI il {dtt(d.generata_il)} su dati cloud FIER e manuali Torri: verificare sempre in campo.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
